/**
 * Typed fetch wrapper for the RoyaltyFlow Pro backend.
 *
 * The backend wraps every payload in an ApiResponse envelope:
 * `{ success, data?, error?, details?, meta? }`. These helpers unwrap the
 * envelope and surface failures as a typed `ApiError`. Paths are relative —
 * the CRA dev proxy (package.json "proxy") routes them to the backend host.
 */

export interface ResponseMeta {
  total: number;
  page: number;
  limit: number;
}

export interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
  /** Structured error context, e.g. per-row CSV issues or field issues. */
  details?: Record<string, unknown>;
  meta?: ResponseMeta;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly details?: Record<string, unknown>;

  constructor(message: string, status: number, details?: Record<string, unknown>, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    // Keeps `instanceof ApiError` working when transpiled to ES5.
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ApiFetchOptions {
  method?: HttpMethod;
  body?: unknown;
  token?: string | null;
  signal?: AbortSignal;
}

export type QueryParams = Record<string, string | number | boolean | undefined>;

/**
 * Builds a query string from a plain filter object, keeping only string,
 * number, and boolean values (undefined and empty strings are skipped).
 */
export function buildQuery(params?: object): string {
  if (!params) {
    return '';
  }

  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]: [string, unknown]) => {
    if (typeof value === 'string' && value !== '') {
      search.set(key, value);
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      search.set(key, String(value));
    }
  });

  const encoded = search.toString();
  return encoded === '' ? '' : `?${encoded}`;
}

async function requestEnvelope<T>(path: string, options: ApiFetchOptions): Promise<ApiEnvelope<T>> {
  const { method = 'GET', body, token, signal } = options;

  const headers: Record<string, string> = { Accept: 'application/json' };

  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  // 204 No Content (deletes, logout) has an empty body — parsing it as JSON
  // would throw and make a successful mutation look failed, skipping
  // react-query cache invalidation.
  if (response.status === 204) {
    return { success: true };
  }

  let envelope: ApiEnvelope<T>;

  try {
    envelope = (await response.json()) as ApiEnvelope<T>;
  } catch {
    throw new ApiError(`Request failed with status ${response.status}`, response.status);
  }

  if (!response.ok || !envelope.success) {
    throw new ApiError(
      envelope.error ?? `Request failed with status ${response.status}`,
      response.status,
      envelope.details,
      envelope.code,
    );
  }

  return envelope;
}

/** Fetches and unwraps `data` from the backend envelope. */
export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const envelope = await requestEnvelope<T>(path, options);
  return envelope.data as T;
}

export interface PaginatedResult<T> {
  data: T;
  meta?: ResponseMeta;
}

/** Fetches and returns both `data` and pagination `meta` from the envelope. */
export async function apiFetchWithMeta<T>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<PaginatedResult<T>> {
  const envelope = await requestEnvelope<T>(path, options);
  return { data: envelope.data as T, meta: envelope.meta };
}
